import { ref, computed } from 'vue';
import { i18n } from '@/plugins/internationalization/i18n';
import type { BackfillSuggestionDTO } from '@/types';
import { isProductionGroupRangeTooShort } from '../production-groups/productionGroupTimeline';

const MS_PER_DAY = 86_400_000;

export type BackfillDraftSource = 'Tally' | 'Telemetry' | 'Manual';
export type BackfillDraftConfidence = 'High' | 'Medium' | 'Low';

// One row of the backfill wizard's suggestion table - a detected suggestion, or a farmer-added
// manual one, both edited/accepted/discarded through the same shape. `id` is a client-only key
// (suggestions from the backend carry no id of their own - they aren't persisted rows), stable
// for the row's lifetime so v-for/table state doesn't reshuffle as other rows are edited.
export type BackfillDraft = {
    id: string;
    start: string;
    end: string | null;
    source: BackfillDraftSource;
    // Manual rows carry no confidence badge - there is nothing to be confident/unsure about in a
    // window the farmer typed in themselves.
    confidence: BackfillDraftConfidence | null;
    note: string | null;
    accepted: boolean;
    // True once the farmer has touched this row themselves - either it was typed in from scratch
    // (addManual) or a detected suggestion's dates were edited (setStart/setEnd). Drives whether the
    // created group is written as auto-generated: an untouched detection stays auto-generated, but a
    // suggestion the farmer edited is no longer purely what detection produced.
    customized: boolean;
};

// How long the window [start, end] runs, counting both endpoints - a same-day window is 1 day.
// Callers substitute today for an open-ended window's missing end before calling.
export function durationDays(start: string, end: string): number {
    return Math.floor((Date.parse(end) - Date.parse(start)) / MS_PER_DAY) + 1;
}

// Inclusive range overlap, open ends substituted with today - the same rule the backend's create
// path rejects on, so a range flagged here is one that would actually fail to create.
export function rangesOverlap(aStart: string, aEnd: string | null, bStart: string, bEnd: string | null, today: string): boolean {
    const aEndMs = Date.parse(aEnd || today);
    const bEndMs = Date.parse(bEnd || today);
    return Date.parse(aStart) <= bEndMs && Date.parse(bStart) <= aEndMs;
}

// Which accepted suggestions would fail to create because their dates collide with an existing
// group or another accepted suggestion - checked against accepted rows only, since an unaccepted
// one is never submitted. Shared by the table (row accent) and the wizard (disabling "Create") so
// the two can't disagree about what counts as a collision.
export function overlappingSuggestionIds(suggestions: BackfillDraft[], existingGroups: { start: string; end: string | null }[], today: string): Set<string> {
    const ids = new Set<string>();
    const acceptedSuggestions = suggestions.filter((s) => s.accepted);

    for (const suggestion of acceptedSuggestions) {
        const overlapsExisting = existingGroups.some((group) => rangesOverlap(suggestion.start, suggestion.end, group.start, group.end, today));
        const overlapsOtherSuggestion = acceptedSuggestions.some((other) => other.id !== suggestion.id && rangesOverlap(suggestion.start, suggestion.end, other.start, other.end, today));
        if (overlapsExisting || overlapsOtherSuggestion) ids.add(suggestion.id);
    }

    return ids;
}

// Same rule every Production Group's dates are held to (see isProductionGroupRangeTooShort). Only
// accepted rows are checked, same reasoning as overlappingSuggestionIds.
export function invalidRangeSuggestionIds(suggestions: BackfillDraft[]): Set<string> {
    return new Set(suggestions.filter((s) => s.accepted && isProductionGroupRangeTooShort(s.start, s.end)).map((s) => s.id));
}

// Days with the whole weeks they contain alongside, which is the unit farmers plan stocking rounds
// in. Partial weeks are dropped rather than rounded: six days is zero weeks, not one.
export function formatDuration(days: number): string {
    return i18n.global.t('views.monitoring.groupOverview.modals.backfill.duration', { days, weeks: Math.floor(days / 7) });
}

let nextManualId = 0;

/**
 * Pure suggestion-list state for the Production Group backfill wizard - accept/discard/edit-dates/
 * manually-add, independent of the chart/table/sticky-bar that render it. Mirrors how
 * useMultiSteps.ts separates step state from step rendering.
 */
export default function useProductionGroupBackfill(today: string) {
    const suggestions = ref<BackfillDraft[]>([]);

    // Replaces the whole list from a fresh backend read - detection re-running (e.g. after a
    // from-date override) invalidates any in-progress edits, same as re-opening the wizard would.
    function load(serverSuggestions: BackfillSuggestionDTO[]) {
        suggestions.value = serverSuggestions.map((suggestion, index) => ({
            id: `server-${index}`,
            start: suggestion.start,
            end: suggestion.end,
            source: suggestion.source,
            confidence: suggestion.confidence,
            note: suggestion.note,
            accepted: true, // bulk-accepted by default
            customized: false,
        }));
    }

    function find(id: string) {
        return suggestions.value.find((s) => s.id === id);
    }

    function setAccepted(id: string, accepted: boolean) {
        const suggestion = find(id);
        if (suggestion) suggestion.accepted = accepted;
    }

    // The table's per-row "discard" action - same effect as unchecking the accept checkbox, kept
    // as its own function so callers don't have to know that.
    function discard(id: string) {
        setAccepted(id, false);
    }

    function setStart(id: string, start: string) {
        const suggestion = find(id);
        if (suggestion) {
            suggestion.start = start;
            suggestion.customized = true;
        }
    }

    function setEnd(id: string, end: string | null) {
        const suggestion = find(id);
        if (suggestion) {
            suggestion.end = end;
            suggestion.customized = true;
        }
    }

    // Appends a new, accepted-by-default, editable row - `source: 'Manual'`, no confidence badge.
    // Defaults to starting the day after whatever history (an existing group or another suggestion)
    // runs latest, so a fresh row doesn't immediately self-overlap and trip the blocked-row warning -
    // `existingGroups` is the caller's (the modal's own `existingGroups` read is what overlap-checking
    // already uses elsewhere, so this stays consistent with it). Falls back to the last week up to
    // today when there is no known history to pick up after, or when the latest-known end is already
    // today or later (an open-ended row), since no gap exists to default into either way.
    function addManual(existingGroups: { start: string; end: string | null }[] = []) {
        const knownEnds = [...suggestions.value.map((s) => s.end || today), ...existingGroups.map((g) => g.end || today)];
        const latestEndMs = knownEnds.length ? Math.max(...knownEnds.map((date) => Date.parse(date))) : null;
        const defaultStart =
            latestEndMs !== null && latestEndMs + MS_PER_DAY < Date.parse(today)
                ? new Date(latestEndMs + MS_PER_DAY).toISOString().slice(0, 10)
                : new Date(Date.parse(today) - 6 * MS_PER_DAY).toISOString().slice(0, 10);
        nextManualId += 1;
        suggestions.value.push({
            id: `manual-${nextManualId}`,
            start: defaultStart,
            end: today,
            source: 'Manual',
            confidence: null,
            note: null,
            accepted: true,
            customized: true,
        });
    }

    const accepted = computed(() => suggestions.value.filter((s) => s.accepted));
    const acceptedCount = computed(() => accepted.value.length);
    const totalDays = computed(() => accepted.value.reduce((sum, s) => sum + durationDays(s.start, s.end || today), 0));

    return {
        suggestions,
        load,
        setAccepted,
        discard,
        setStart,
        setEnd,
        addManual,
        accepted,
        acceptedCount,
        totalDays,
    };
}
